import { useMemo, useState } from "react";
import { Check, ChevronDown, GraduationCap, Sparkle } from "lucide-react";
import { cn } from "@/utils/helpers";
import type { MarketSignal } from "@/types/marketSignal.type";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { formatFullDate, formatShortDate, kindLabelSingular } from "./signalMeta";
import { scrubVendorTerms } from "@/lib/vendorTerms";
import { citationOf } from "./evidenceDisplay";
import SourceFavicon from "./SourceFavicon";

const FILTERS = ["all", "graduated", "expired", "closed"] as const;
type Filter = (typeof FILTERS)[number];

const FILTER_LABELS: Record<Filter, string> = {
  all: "All",
  graduated: "Graduated",
  expired: "Expired",
  closed: "Declined or dismissed",
};

/** Which outcome filter a resolved signal belongs to. Superseded signals were
 *  taken off the board without graduating, so they sit with the dismissed. */
function bucketOf(signal: MarketSignal): Filter | null {
  if (signal.status === "graduated") return "graduated";
  if (signal.status === "expired") return "expired";
  if (signal.status === "dismissed" || signal.status === "superseded") return "closed";
  return null;
}

type Tone = "accent" | "faint" | "line";

/** The outcome in words that do not repeat the filter it sits under. */
function outcomeOf(signal: MarketSignal): { label: string; tone: Tone } {
  switch (signal.status) {
    case "graduated":
      return { label: "Graduated to Brand DNA", tone: "accent" };
    case "expired":
      return { label: "Window closed", tone: "faint" };
    case "superseded":
      return { label: "Superseded", tone: "faint" };
    case "dismissed":
      return signal.acceptedAt
        ? { label: "Dismissed while steering", tone: "line" }
        : { label: "Declined", tone: "line" };
    default:
      return { label: signal.status, tone: "faint" };
  }
}

const PAGE_SIZE = 20;

/**
 * The date that actually matters per outcome. `updatedAt` is Prisma's "last
 * touched" stamp, so using it for everything mislabels the trail: a graduated
 * signal reports its graduation date, an expired one the day its window closed.
 * Everything else falls back to updatedAt.
 */
function trailDate(signal: MarketSignal): string {
  if (signal.status === "graduated" && signal.graduatedAt) return signal.graduatedAt;
  if (signal.status === "expired" && signal.validUntil) return signal.validUntil;
  return signal.updatedAt;
}

function newestFirst(signals: MarketSignal[]): MarketSignal[] {
  return [...signals].sort(
    (a, b) => new Date(trailDate(b)).getTime() - new Date(trailDate(a)).getTime(),
  );
}

/**
 * HistoryTrail: the ledger of resolved signals, newest first, as one table.
 *
 * Every row is a disclosure that opens onto the signal's real body, its
 * confidence, its sources and the window it ran for. All of that already sits in
 * the same React Query cache the list renders from, so opening a row costs no
 * request. Graduate (expired rows only) runs through the same inline confirm the
 * steering rows use, because it mints a permanent Brand DNA fact and is one-way.
 *
 * `initialFocusId` opens the trail on one signal: the filter resets to All, the
 * page is the one that holds it, and its row starts open. The parent remounts
 * the trail (by key) to ask again, so none of this is replayed from an effect.
 */
export default function HistoryTrail({
  signals,
  allSignals,
  memberNames,
  canEdit,
  isBusy,
  onGraduate,
  onOpenSources,
  initialFocusId = null,
}: {
  /** Resolved signals only (expired / graduated / superseded / dismissed). */
  signals: MarketSignal[];
  /** Every signal, for resolving supersededBySignalId to its successor. */
  allSignals: MarketSignal[];
  memberNames: Map<string, string>;
  canEdit: boolean;
  isBusy: (id: string) => boolean;
  onGraduate: (signal: MarketSignal) => void;
  onOpenSources: (signal: MarketSignal) => void;
  initialFocusId?: string | null;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(() => {
    if (!initialFocusId) return 0;
    const at = newestFirst(signals).findIndex((s) => s.id === initialFocusId);
    return at >= 0 ? Math.floor(at / PAGE_SIZE) : 0;
  });
  const [openId, setOpenId] = useState<string | null>(initialFocusId);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const byId = useMemo(() => {
    const map = new Map<string, MarketSignal>();
    for (const s of allSignals) map.set(s.id, s);
    return map;
  }, [allSignals]);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: signals.length, graduated: 0, expired: 0, closed: 0 };
    for (const s of signals) {
      const b = bucketOf(s);
      if (b) c[b] += 1;
    }
    return c;
  }, [signals]);

  const sorted = useMemo(
    () => newestFirst(signals.filter((s) => filter === "all" || bucketOf(s) === filter)),
    [signals, filter],
  );

  // Clamp defensively, so Previous/Next never burn clicks on a raw index that is
  // past the end of a shrunk list.
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const rows = sorted.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);
  const from = sorted.length === 0 ? 0 : currentPage * PAGE_SIZE + 1;
  const to = Math.min(from + PAGE_SIZE - 1, sorted.length);

  const pickFilter = (next: Filter) => {
    setFilter(next);
    setPage(0);
    setOpenId(null);
    setConfirmingId(null);
  };

  const toggleRow = (id: string) => {
    setConfirmingId(null);
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="sig-hist">
      {/* The backend returns every status in one payload, so this is a
          client-side narrowing, not a refetch. */}
      <div className="sig-chips" role="group" aria-label="Filter by outcome">
        {FILTERS.map((f) => {
          const n = counts[f];
          if (f !== "all" && n === 0) return null;
          return (
            <button
              key={f}
              type="button"
              className="sig-chip"
              aria-pressed={filter === f}
              onClick={() => pickFilter(f)}
            >
              {/* The phone's filter chips mark the one that is on with a check. */}
              {filter === f ? <Check className="sig-chip__check" aria-hidden="true" /> : null}
              <span className="sig-chip__label">{FILTER_LABELS[f]}</span>
              <span className="sig-chip__count">{n}</span>
            </button>
          );
        })}
      </div>

      {sorted.length > 0 ? (
        <div className="sig-table">
          <div className="sig-table__head" aria-hidden="true">
            <span>Date</span>
            <span>Outcome</span>
            <span>Signal</span>
            <span>Accepted by</span>
            <span />
          </div>

          {rows.map((s) => {
            const outcome = outcomeOf(s);
            const open = openId === s.id;
            const busy = isBusy(s.id);
            const curator = s.acceptedByUserId ? memberNames.get(s.acceptedByUserId) : undefined;
            const successor = s.supersededBySignalId ? byId.get(s.supersededBySignalId) : undefined;
            const canGraduate = canEdit && s.status === "expired";
            const bodyId = `sig-hist-body-${s.id}`;

            return (
              <div
                key={s.id}
                id={`sig-${s.id}`}
                tabIndex={-1}
                className={cn("sig-table__row", open && "is-open")}
              >
                <button
                  type="button"
                  className="sig-table__line"
                  aria-expanded={open}
                  aria-controls={bodyId}
                  onClick={() => toggleRow(s.id)}
                >
                  <span className="sig-table__date">{formatFullDate(trailDate(s))}</span>
                  <span className={cn("sig-table__outcome", `is-${outcome.tone}`)}>
                    <span className="sig-table__dot" aria-hidden="true" />
                    {outcome.label}
                  </span>
                  <span className="sig-table__sig">
                    <span className="sig-table__title" dir="auto">
                      {s.title}
                    </span>
                    <span className="sig-table__kind">{kindLabelSingular(s.kind)}</span>
                  </span>
                  <span className={cn("sig-table__who", !curator && "is-none")}>
                    {curator ?? (s.acceptedByUserId ? "Name unavailable" : "Not accepted")}
                  </span>
                  <ChevronDown className="sig-table__chev" aria-hidden="true" />
                </button>

                {open ? (
                  <div className="sig-table__detail" id={bodyId}>
                    {/* Signals / History row · Opened: one mono line, then the sources as
                        favicon, host and title, then the action with its note. */}
                    <p className="sig-table__meta">
                      {[
                        s.confidence != null ? `${Math.round(s.confidence * 100)}% confidence` : null,
                        s.groundingObservations.length > 0
                          ? `${s.groundingObservations.length} ${s.groundingObservations.length === 1 ? "source" : "sources"}`
                          : null,
                        s.validUntil
                          ? `Ran ${formatShortDate(s.validFrom)} to ${formatShortDate(s.validUntil)}`
                          : `Ran from ${formatShortDate(s.validFrom)}, open-ended`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>

                    {s.groundingObservations.length > 0 ? (
                      <ul className="sig-table__sources">
                        {s.groundingObservations.map((o) => {
                          const c = citationOf(o);
                          return (
                            <li key={o.id}>
                              <button
                                type="button"
                                className="sig-table__source"
                                onClick={() => onOpenSources(s)}
                              >
                                <SourceFavicon host={c.host} size={13} />
                                <span className="sig-table__source-host">{c.host ?? "Source"}</span>
                                {c.title ? (
                                  <span className="sig-table__source-title" dir="auto">
                                    {scrubVendorTerms(c.title)}
                                  </span>
                                ) : null}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}

                    {successor ? (
                      <p className="sig-table__link" dir="auto">
                        Replaced by <b>{successor.title}</b>
                      </p>
                    ) : null}
                    {s.status === "graduated" ? (
                      <p className="sig-table__link">
                        <Sparkle aria-hidden="true" />
                        Promoted into permanent Brand DNA
                        {s.graduatedAt ? ` on ${formatFullDate(s.graduatedAt)}` : ""}
                      </p>
                    ) : null}

                    {canGraduate ? (
                      <div className="sig-table__acts">
                        {confirmingId === s.id ? (
                          <>
                            <span className="sig-steer__confirm">
                              <Sparkle aria-hidden="true" />
                              Promote to permanent Brand DNA?
                            </span>
                            <KitButton
                              variant="ghost"
                              isDisabled={busy}
                              onClick={() => setConfirmingId(null)}
                            >
                              Cancel
                            </KitButton>
                            <KitButton isDisabled={busy} onClick={() => onGraduate(s)}>
                              Confirm
                            </KitButton>
                          </>
                        ) : (
                          <>
                            <KitButton
                              variant="secondary"
                              iconRight={GraduationCap}
                              isDisabled={busy}
                              onClick={() => setConfirmingId(s.id)}
                              /* Per-row accessible name: a page of buttons all
                                 called "Graduate" is indistinguishable to a
                                 screen reader. */
                              aria-label={`Graduate to Brand DNA: ${s.title}`}
                            >
                              Graduate
                            </KitButton>
                            <span className="sig-table__note">
                              {s.validUntil ? `Its window closed on ${formatShortDate(s.validUntil)}. ` : null}
                              Graduating makes it a permanent fact in the universe.
                            </span>
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="sig-helper">Nothing here yet under this filter.</p>
      )}

      {sorted.length > PAGE_SIZE ? (
        <nav className="sig-pager" aria-label="History pages">
          <span className="sig-pager__status">
            {from}
            {"–"}
            {to} of {sorted.length}
          </span>
          <span className="sig-pager__nav">
            <KitButton
              variant="secondary"
              className="sig-btn"
              isDisabled={currentPage === 0}
              onClick={() => setPage(Math.max(0, currentPage - 1))}
            >
              Previous
            </KitButton>
            <span className="sig-pager__page">
              Page {currentPage + 1} of {pageCount}
            </span>
            <KitButton
              variant="secondary"
              className="sig-btn"
              isDisabled={currentPage >= pageCount - 1}
              onClick={() => setPage(Math.min(pageCount - 1, currentPage + 1))}
            >
              Next
            </KitButton>
          </span>
        </nav>
      ) : null}
    </div>
  );
}
